import React, { FC, useEffect, useState } from "react";
import {
  ChipOptionsDataType,
  FormController,
  MuiBox,
  MuiDateFieldFormController,
  MuiGrid,
  MuiOptionMenu,
  MuiSelect,
  MuiTable,
  MuiTextField,
} from "../../../../../../components";
import { useSiteCore } from "../../../../../../hooks/useSiteCore";
import {
  ActivitiesResultFormKey,
  ActivityItemType,
  TopicResponseType,
} from "../../../../modelsAndSchemas/Models";
import { InputAdornment } from "@mui/material";
import { Search } from "@mui/icons-material";
import { MAX_LENGTH_50 } from "../../../../../../helpers/constants";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { siteCoreContentLoadingSelector } from "../../../../../../redux/slices/appState/appStateSelector";
import { activityTimelineSiteCoreKeys } from "../../../../../createProgram/siteCore/SiteCoreKeys";
import MuiChipWithPopover from "../../../../../../components/chipWithPopover/MuiChipWithPopover";
import { isNotEmpty } from "../../../../../../helpers/utils";
import { MuiNotesToolTip } from "../../../../../../components/tooltip/notesToolTip/MuiNotesToolTip";
import { LabelValue } from "../../../../../../helpers/types/common";
import { useNavigationSiteCore } from "../../../../../../hooks/useNavigationSiteCore";
import { getDateFormatByCountry } from "../../../../../../helpers/constants/countryDateFormat";
import { useGetLoggedInUserDetails } from "../../../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import dayjs from "dayjs";

interface ActivitiesResultProps {
  pageId: string;
  rows: ActivityItemType[];
  handleEdit?: (id: number) => void;
  handleDelete?: (id: number) => void;
}
export const ActivitiesResult: FC<ActivitiesResultProps> = ({
  pageId,
  rows,
  handleEdit,
  handleDelete,
}) => {
  const { fetchLabel, commonLabels, fetchValueByKeyOfDropdown } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();

  const tableRef = React.useRef<{ resetPagination: () => void }>(null);

  const siteCoreLoading = useSelector(siteCoreContentLoadingSelector);

  const [uniqueModality, setUniqueModality] = useState<LabelValue[]>([]);

  const [filteredResults, setFilteredResults] = useState<ActivityItemType[]>(
    [],
  );
  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    loggedInUser?.countryCode ?? "",
  );
  // updated sitecore data
  const [allUpdatedRows, setAllUpdatedRows] = useState<ActivityItemType[]>([]);

  const { control, watch, getValues, reset } = useForm({
    mode: "all",
    defaultValues: {
      [ActivitiesResultFormKey.activityTypeName]: "",
      [ActivitiesResultFormKey.modality]: "",
      [ActivitiesResultFormKey.topic]: "",
      [ActivitiesResultFormKey.onBehalfOfCoachName]: "",
      [ActivitiesResultFormKey.activityDate]: "",
    },
  });

  useEffect(() => {
    if (!siteCoreLoading) {
      const localData: ActivityItemType[] = JSON.parse(JSON.stringify(rows));
      localData.forEach((data) => {
        data.actualDurationMinsName = fetchValueByKeyOfDropdown(
          activityTimelineSiteCoreKeys.activityTimeline,
          activityTimelineSiteCoreKeys.actualDurationMinutesList,
          data.actualDurationInMinutes,
        );
        data.defaultDurationMinsName = data.defaultDurationInMinutes;
        data.modalityName = fetchValueByKeyOfDropdown(
          activityTimelineSiteCoreKeys.activityTimeline,
          activityTimelineSiteCoreKeys.modalityList,
          data.modality,
        );
      });
      setAllUpdatedRows(localData);
      setFilteredResults([...localData]);
    }
  }, [rows, siteCoreLoading]);

  const getLabel = (key: string) => {
    return fetchLabel(activityTimelineSiteCoreKeys.activityTimeline, key);
  };

  useEffect(() => {
    resetPagination();

    const myData = [...rows];

    const uniqueModality = Array.from(
      new Set(
        myData.filter((item) => item.modality).map((item) => item.modality),
      ),
    ).map((modality) => ({
      value: modality,
      label: fetchValueByKeyOfDropdown(
        activityTimelineSiteCoreKeys.activityTimeline,
        activityTimelineSiteCoreKeys.modalityList,
        modality,
      ),
    }));

    setUniqueModality(uniqueModality);
    reset();
  }, [rows]);

  const applyFilters = (data: ActivityItemType[]) => {
    const filters = getValues();

    return data.filter((item) => {
      return Object.keys(filters).every((key) => {
        const filterValue = filters[key as keyof typeof filters];

        const filterValueStr = filterValue.toString().toLowerCase();
        const itemOfKey = item[key as keyof ActivityItemType];
        if (itemOfKey === undefined && filterValue !== "") {
          return false;
        }

        // Skip empty filters
        if (filterValue === "") return true;

        if (key === ActivitiesResultFormKey.topic) {
          return filterTopics(item[key], filterValueStr);
        } else {
          const itemValue = itemOfKey?.toString().toLowerCase();
          return itemValue?.includes(filterValueStr);
        }
      });
    });
  };

  const filterTopics = (
    topics: TopicResponseType[],
    filterValueStr: string,
  ) => {
    return topics?.some((topicItem) =>
      topicItem.topic?.toLowerCase().includes(filterValueStr),
    );
  };

  useEffect(() => {
    if (isNotEmpty(allUpdatedRows)) {
      const filteredData = applyFilters(allUpdatedRows);
      setFilteredResults(filteredData);
      resetPagination();
    }
  }, [
    watch(ActivitiesResultFormKey.activityTypeName),
    watch(ActivitiesResultFormKey.modality),
    watch(ActivitiesResultFormKey.topic),
    watch(ActivitiesResultFormKey.onBehalfOfCoachName),
  ]);

  const resetPagination = () => {
    if (tableRef.current) {
      tableRef.current.resetPagination();
    }
  };

  const tableHeaderForm = [
    <FormController
      control={control}
      name={ActivitiesResultFormKey.activityTypeName}
      key={ActivitiesResultFormKey.activityTypeName}
    >
      <MuiTextField
        id={`${pageId}-${ActivitiesResultFormKey.activityTypeName}`}
        startAdornment={
          <InputAdornment position="start">
            <Search />
          </InputAdornment>
        }
        aria-label={commonLabels.search}
        showCloseIcon={true}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      key={ActivitiesResultFormKey.modality}
      name={ActivitiesResultFormKey.modality}
    >
      <MuiSelect
        label=""
        visibleDefaultSelectOption={true}
        options={uniqueModality}
        value=""
        placeholder={commonLabels.selectPlaceHolder}
        id={`${pageId}-${ActivitiesResultFormKey.modality}`}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={ActivitiesResultFormKey.topic}
      key={ActivitiesResultFormKey.topic}
    >
      <MuiTextField
        id={`${pageId}-${ActivitiesResultFormKey.topic}`}
        startAdornment={
          <InputAdornment position="start">
            <Search />
          </InputAdornment>
        }
        aria-label={commonLabels.search}
        showCloseIcon={true}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <></>,
    <></>,
    <MuiDateFieldFormController
      control={control}
      name={ActivitiesResultFormKey.activityDate}
      label={""}
      valueFormat={userCountryDateFormat}
      clearable
      format={userCountryDateFormat}
      maxDate={dayjs()}
      id={`${pageId}-${ActivitiesResultFormKey.activityDate}`}
      disableManualInput
      selectDateARIALabel={commonARIALabel.selectDate}
      requiredARIALabel={""}
    />,

    <FormController
      control={control}
      name={ActivitiesResultFormKey.onBehalfOfCoachName}
      key={ActivitiesResultFormKey.onBehalfOfCoachName}
    >
      <MuiTextField
        id={`${pageId}-${ActivitiesResultFormKey.onBehalfOfCoachName}`}
        startAdornment={
          <InputAdornment position="start">
            <Search />
          </InputAdornment>
        }
        aria-label={commonLabels.search}
        showCloseIcon={true}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <></>,
    <></>,
  ];

  const columns = [
    {
      id: ActivitiesResultFormKey.activityTypeName,
      label: getLabel(activityTimelineSiteCoreKeys.activityType),
      isSortVisible: true,
      className: "cell-activity-type",
    },
    {
      id: ActivitiesResultFormKey.modalityName,
      label: getLabel(activityTimelineSiteCoreKeys.modality),
      isSortVisible: true,
      className: "cell-modality",
    },
    {
      id: ActivitiesResultFormKey.topic,
      label: getLabel(activityTimelineSiteCoreKeys.topic),
      isSortVisible: true,
      className: "cell-topics",
      render: (row: any) => (
        <MuiChipWithPopover
          data={
            row.topic.map((item: any) => ({
              label: item.topic,
              value: item.id,
            })) as ChipOptionsDataType[]
          }
          handleDelete={() => {}}
          id={"selected-topic"}
          isEditable={false}
          headerLabel={getLabel(activityTimelineSiteCoreKeys.topic)}
          gridRef={React.createRef<HTMLElement>()}
          countToWrap={1}
          showMoreARIALabel={commonARIALabel.showMoreDetails}
          removeChipNameARIALabel={commonARIALabel.removeChipName}
          closePopoverARIALabel={commonARIALabel.closePopover}
        />
      ),
    },
    {
      id: ActivitiesResultFormKey.defaultDurationMinsName,
      label: getLabel(activityTimelineSiteCoreKeys.defaultDuration),
      isSortVisible: true,
      className: "cell-activity-class",
    },
    {
      id: ActivitiesResultFormKey.actualDurationMinsName,
      label: getLabel(activityTimelineSiteCoreKeys.actualDuration),
      isSortVisible: true,
      className: "cell-activity-status",
    },
    {
      id: ActivitiesResultFormKey.activityDate,
      label: getLabel(activityTimelineSiteCoreKeys.activityDate),
      isSortVisible: true,
      className: "cell-activity-status",
    },
    {
      id: ActivitiesResultFormKey.onBehalfOfCoachName,
      label: getLabel(activityTimelineSiteCoreKeys.onBehalfOfCoachId),
      isSortVisible: true,
    },
    {
      id: ActivitiesResultFormKey.notes,
      label: getLabel(activityTimelineSiteCoreKeys.notesLabel),
      isSortVisible: false,
      render: (row: any) => (
        <MuiBox>
          <MuiNotesToolTip message={row?.notes} />
        </MuiBox>
      ),
    },
    {
      id: "action",
      label: getLabel(activityTimelineSiteCoreKeys.actionHeader),
      className: "cell-actions",
      render: (row: any) => {
        return (
          <MuiBox>
            <MuiOptionMenu
              menuItems={[
                {
                  onClick: () => handleEdit?.(row.id),
                  dataTestId: `${pageId}-edit-activity`,
                  label: commonLabels.edit,
                  ariaLabel: commonLabels.edit,
                },
                {
                  onClick: () => handleDelete?.(row.id),
                  dataTestId: `${pageId}-delete-activity`,
                  label: commonLabels.delete,
                  ariaLabel: commonLabels.delete,
                },
              ]}
            />
          </MuiBox>
        );
      },
    },
  ];

  const getColumns = () => {
    if (rows.length === 0) {
      return columns.slice(0, -1); // remove the last column
    }
    return columns;
  };

  const getTableHeaderForm = () => {
    if (rows.length === 0) {
      return tableHeaderForm.slice(0, -1); // remove the last column
    }
    return tableHeaderForm;
  };

  return (
    <MuiGrid container data-testid={`${pageId}-bulk-activity-results`}>
      <MuiGrid size={{ xs: 12 }}>
        <MuiTable
          ref={tableRef}
          pageId={pageId}
          tableHeaderForm={getTableHeaderForm()}
          columns={getColumns()}
          tableResults={[...filteredResults]}
          noDataInApi={rows.length === 0}
          noResultMessage={commonLabels.noResult}
          emptyMessage={getLabel(
            activityTimelineSiteCoreKeys.addActivitysProceedMessage,
          )}
          priorityResults={[]}
          isLastColumnSticky={rows.length > 0}
        />
      </MuiGrid>
    </MuiGrid>
  );
};
